import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Home, Users, Calendar, BookOpen, ShoppingBag, Clock,
  Plus, Edit, Trash2, Check, X, ChevronRight, FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dashboard Overview
const Dashboard = () => {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [childrenRes, enrollmentsRes] = await Promise.all([
        axios.get(`${API}/children`),
        axios.get(`${API}/enrollments`)
      ]);
      setChildren(childrenRes.data);
      setEnrollments(enrollmentsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-bold text-slate-900">
        Welcome, {user?.full_name?.split(' ')[0]}!
      </h1>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="card-plain">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <p className="font-body text-slate-500 text-sm">Children</p>
              <p className="font-heading text-2xl font-bold text-slate-900">{children.length}</p>
            </div>
          </div>
        </div>
        <div className="card-plain">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-pink-500" />
            </div>
            <div>
              <p className="font-body text-slate-500 text-sm">Active Enrollments</p>
              <p className="font-heading text-2xl font-bold text-slate-900">
                {enrollments.filter(e => e.status === 'confirmed').length}
              </p>
            </div>
          </div>
        </div>
        <div className="card-plain">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="font-body text-slate-500 text-sm">This Term</p>
              <p className="font-heading text-2xl font-bold text-slate-900">Spring 2026</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card-plain">
        <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Quick Actions</h2>
        <div className="grid md:grid-cols-2 gap-3">
          <Link to="/portal/children" className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
            <span className="font-body font-semibold text-slate-700">Manage Children</span>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </Link>
          <Link to="/portal/enroll" className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
            <span className="font-body font-semibold text-slate-700">Enroll in Class</span>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </Link>
          <Link to="/portal/summaries" className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
            <span className="font-body font-semibold text-slate-700">Weekly Summaries</span>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </Link>
          <Link to="/shop" className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
            <span className="font-body font-semibold text-slate-700">Materials Shop</span>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </Link>
        </div>
      </div>

      {/* Recent Enrollments */}
      {enrollments.length > 0 && (
        <div className="card-plain">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Your Enrollments</h2>
          <div className="space-y-3">
            {enrollments.slice(0, 5).map(enrollment => (
              <div key={enrollment.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div>
                  <p className="font-heading font-bold text-slate-900">{enrollment.child_name}</p>
                  <p className="font-body text-sm text-slate-600">
                    {enrollment.class_name || enrollment.workshop_name}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  enrollment.status === 'confirmed' 
                    ? 'bg-teal-100 text-teal-700'
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {enrollment.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Children Management
const ChildrenPage = () => {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const [formData, setFormData] = useState({
    name: '', age: '', year_group: '', school_class: '',
    parent_guardian_name: '', email: '', phone: '',
    emergency_contact: '', medical_conditions: '',
    first_aid_permission: false, photo_permission: false, photo_url: ''
  });

  const fetchChildren = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/children`);
      setChildren(response.data);
    } catch (error) {
      console.error('Failed to fetch children:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChildren();
  }, [fetchChildren]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        age: parseInt(formData.age),
        year_group: parseInt(formData.year_group)
      };
      
      if (editingChild) {
        await axios.put(`${API}/children/${editingChild.id}`, data);
        toast.success('Child profile updated');
      } else {
        await axios.post(`${API}/children`, data);
        toast.success('Child profile created');
      }
      fetchChildren();
      resetForm();
    } catch (error) {
      toast.error('Failed to save child profile');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this child profile?')) {
      try {
        await axios.delete(`${API}/children/${id}`);
        toast.success('Child profile deleted');
        fetchChildren();
      } catch (error) {
        toast.error('Failed to delete child profile');
      }
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingChild(null);
    setFormData({
      name: '', age: '', year_group: '', school_class: '',
      parent_guardian_name: '', email: '', phone: '',
      emergency_contact: '', medical_conditions: '',
      first_aid_permission: false, photo_permission: false, photo_url: ''
    });
  };

  const startEdit = (child) => {
    setEditingChild(child);
    setFormData({
      name: child.name,
      age: child.age.toString(),
      year_group: child.year_group.toString(),
      school_class: child.school_class,
      parent_guardian_name: child.parent_guardian_name,
      email: child.email,
      phone: child.phone,
      emergency_contact: child.emergency_contact,
      medical_conditions: child.medical_conditions || '',
      first_aid_permission: child.first_aid_permission,
      photo_permission: child.photo_permission,
      photo_url: child.photo_url || ''
    });
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold text-slate-900">My Children</h1>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="add-child-btn">
          <Plus className="w-5 h-5" /> Add Child
        </button>
      </div>

      {showForm && (
        <div className="card-plain">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">
            {editingChild ? 'Edit Child Profile' : 'Add New Child'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Child's Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="input-artsy"
                  required
                  data-testid="child-name"
                />
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Age *</label>
                <input
                  type="number"
                  value={formData.age}
                  onChange={(e) => setFormData({...formData, age: e.target.value})}
                  className="input-artsy"
                  min="5" max="12"
                  required
                  data-testid="child-age"
                />
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Year Group *</label>
                <select
                  value={formData.year_group}
                  onChange={(e) => setFormData({...formData, year_group: e.target.value})}
                  className="input-artsy"
                  required
                  data-testid="child-year"
                >
                  <option value="">Select year</option>
                  {[1,2,3,4,5,6].map(y => (
                    <option key={y} value={y}>Year {y}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">School Class *</label>
                <input
                  type="text"
                  value={formData.school_class}
                  onChange={(e) => setFormData({...formData, school_class: e.target.value})}
                  className="input-artsy"
                  placeholder="e.g., 3B"
                  required
                  data-testid="child-class"
                />
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Parent/Guardian Name *</label>
                <input
                  type="text"
                  value={formData.parent_guardian_name}
                  onChange={(e) => setFormData({...formData, parent_guardian_name: e.target.value})}
                  className="input-artsy"
                  required
                  data-testid="parent-name"
                />
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="input-artsy"
                  required
                  data-testid="parent-email"
                />
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Phone *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="input-artsy"
                  required
                  data-testid="parent-phone"
                />
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Emergency Contact *</label>
                <input
                  type="text"
                  value={formData.emergency_contact}
                  onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
                  className="input-artsy"
                  placeholder="Name & phone"
                  required
                  data-testid="emergency-contact"
                />
              </div>
            </div>
            <div>
              <label className="block font-body font-semibold text-slate-700 mb-1">Medical Conditions / Allergies</label>
              <textarea
                value={formData.medical_conditions}
                onChange={(e) => setFormData({...formData, medical_conditions: e.target.value})}
                className="input-artsy"
                rows="2"
                placeholder="List any medical conditions or allergies"
                data-testid="medical-conditions"
              />
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.first_aid_permission}
                  onChange={(e) => setFormData({...formData, first_aid_permission: e.target.checked})}
                  className="w-5 h-5 rounded border-slate-300"
                  data-testid="first-aid-permission"
                />
                <span className="font-body text-slate-700">First Aid Permission</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.photo_permission}
                  onChange={(e) => setFormData({...formData, photo_permission: e.target.checked})}
                  className="w-5 h-5 rounded border-slate-300"
                  data-testid="photo-permission"
                />
                <span className="font-body text-slate-700">Photo Permission</span>
              </label>
            </div>
            <div className="flex gap-3">
              <button type="submit" className="btn-primary" data-testid="save-child">
                {editingChild ? 'Update' : 'Save'} Child
              </button>
              <button type="button" onClick={resetForm} className="btn-outline">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {children.length === 0 ? (
        <div className="card-plain text-center py-12">
          <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="font-body text-slate-600 mb-4">No children added yet</p>
          <button onClick={() => setShowForm(true)} className="btn-primary">
            Add Your First Child
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {children.map(child => (
            <div key={child.id} className="card-plain" data-testid={`child-card-${child.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-heading text-xl font-bold text-slate-900">{child.name}</h3>
                  <p className="font-body text-slate-600">Year {child.year_group} • Age {child.age}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => startEdit(child)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid={`edit-child-${child.id}`}>
                    <Edit className="w-5 h-5 text-slate-600" />
                  </button>
                  <button onClick={() => handleDelete(child.id)} className="p-2 hover:bg-red-50 rounded-lg" data-testid={`delete-child-${child.id}`}>
                    <Trash2 className="w-5 h-5 text-red-500" />
                  </button>
                </div>
              </div>
              <div className="space-y-2 font-body text-sm text-slate-600">
                <p><span className="font-semibold">Class:</span> {child.school_class}</p>
                <p><span className="font-semibold">Parent:</span> {child.parent_guardian_name}</p>
                <p><span className="font-semibold">Emergency:</span> {child.emergency_contact}</p>
                <div className="flex gap-4 pt-2">
                  {child.first_aid_permission && (
                    <span className="flex items-center gap-1 text-teal-600">
                      <Check className="w-4 h-4" /> First Aid
                    </span>
                  )}
                  {child.photo_permission && (
                    <span className="flex items-center gap-1 text-teal-600">
                      <Check className="w-4 h-4" /> Photos
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Enrollment Page
const EnrollPage = () => {
  const [children, setChildren] = useState([]);
  const [classes, setClasses] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChild, setSelectedChild] = useState('');
  const [enrollType, setEnrollType] = useState('class');
  const [selectedItem, setSelectedItem] = useState('');
  const [isSibling, setIsSibling] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const { token } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const [childrenRes, classesRes, workshopsRes] = await Promise.all([
        axios.get(`${API}/children`),
        axios.get(`${API}/classes`),
        axios.get(`${API}/workshops`)
      ]);
      setChildren(childrenRes.data);
      setClasses(classesRes.data);
      setWorkshops(workshopsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const calculatePrice = () => {
    if (enrollType === 'class' && selectedItem) {
      const cls = classes.find(c => c.id === selectedItem);
      if (cls) {
        if (isReturning) return cls.returning_student_price;
        if (isSibling) return cls.sibling_price_per_session * cls.num_weeks;
        return cls.price_per_session * cls.num_weeks;
      }
    }
    if (enrollType === 'workshop' && selectedItem) {
      const ws = workshops.find(w => w.id === selectedItem);
      return ws?.price || 0;
    }
    return 0;
  };

  const handleCheckout = async () => {
    if (!selectedChild || !selectedItem) {
      toast.error('Please select a child and class/workshop');
      return;
    }

    setCheckoutLoading(true);

    try {
      const payload = {
        enrollment_type: enrollType,
        child_id: selectedChild,
        origin_url: window.location.origin
      };

      if (enrollType === 'class') {
        payload.class_id = selectedItem;
        payload.is_sibling = isSibling;
        payload.is_returning_student = isReturning;
      } else {
        payload.workshop_id = selectedItem;
      }

      const response = await axios.post(
        `${API}/payments/create-checkout`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div className="card-plain text-center py-12">
        <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">Add a Child First</h2>
        <p className="font-body text-slate-600 mb-4">You need to add a child profile before enrolling.</p>
        <Link to="/portal/children" className="btn-primary">Add Child</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-bold text-slate-900">Enroll in Class or Workshop</h1>

      <div className="card-plain space-y-6">
        {/* Select Child */}
        <div>
          <label className="block font-body font-semibold text-slate-700 mb-2">Select Child</label>
          <select
            value={selectedChild}
            onChange={(e) => setSelectedChild(e.target.value)}
            className="input-artsy"
            data-testid="select-child"
          >
            <option value="">Choose a child</option>
            {children.map(child => (
              <option key={child.id} value={child.id}>{child.name} (Year {child.year_group})</option>
            ))}
          </select>
        </div>

        {/* Select Type */}
        <div>
          <label className="block font-body font-semibold text-slate-700 mb-2">Enrollment Type</label>
          <div className="flex gap-4">
            <button
              onClick={() => { setEnrollType('class'); setSelectedItem(''); }}
              className={`px-6 py-3 rounded-xl font-body font-semibold transition-all ${
                enrollType === 'class' 
                  ? 'bg-teal-600 text-white' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              data-testid="select-class-type"
            >
              Art Class
            </button>
            <button
              onClick={() => { setEnrollType('workshop'); setSelectedItem(''); setIsSibling(false); setIsReturning(false); }}
              className={`px-6 py-3 rounded-xl font-body font-semibold transition-all ${
                enrollType === 'workshop' 
                  ? 'bg-pink-500 text-white' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              data-testid="select-workshop-type"
            >
              Workshop
            </button>
          </div>
        </div>

        {/* Select Class/Workshop */}
        <div>
          <label className="block font-body font-semibold text-slate-700 mb-2">
            Select {enrollType === 'class' ? 'Class' : 'Workshop'}
          </label>
          <select
            value={selectedItem}
            onChange={(e) => setSelectedItem(e.target.value)}
            className="input-artsy"
            data-testid="select-item"
          >
            <option value="">Choose {enrollType === 'class' ? 'a class' : 'a workshop'}</option>
            {(enrollType === 'class' ? classes : workshops).map(item => (
              <option key={item.id} value={item.id} disabled={item.is_full}>
                {item.name} {item.is_full ? '(Full)' : ''} - £{enrollType === 'class' ? item.total_price : item.price}
              </option>
            ))}
          </select>
        </div>

        {/* Discounts (Class only) */}
        {enrollType === 'class' && (
          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isSibling}
                onChange={(e) => { setIsSibling(e.target.checked); if (e.target.checked) setIsReturning(false); }}
                className="w-5 h-5 rounded"
                data-testid="sibling-discount"
              />
              <span className="font-body text-slate-700">Sibling discount (£7/session instead of £8)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isReturning}
                onChange={(e) => { setIsReturning(e.target.checked); if (e.target.checked) setIsSibling(false); }}
                className="w-5 h-5 rounded"
                data-testid="returning-discount"
              />
              <span className="font-body text-slate-700">Returning student (£62/term)</span>
            </label>
          </div>
        )}

        {/* Price Summary */}
        {selectedItem && (
          <div className="bg-slate-50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <span className="font-body text-slate-700">Total Price:</span>
              <span className="font-heading text-2xl font-bold text-teal-600">£{calculatePrice()}</span>
            </div>
          </div>
        )}

        {/* Checkout Button */}
        <button
          onClick={handleCheckout}
          disabled={!selectedChild || !selectedItem || checkoutLoading}
          className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="enroll-checkout"
        >
          {checkoutLoading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
          ) : (
            'Proceed to Payment'
          )}
        </button>
      </div>
    </div>
  );
};

// Weekly Summaries Page
const SummariesPage = () => {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSummaries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/weekly-summaries`);
      setSummaries(response.data);
    } catch (error) {
      console.error('Failed to fetch summaries:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummaries();
  }, [fetchSummaries]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-bold text-slate-900">Weekly Summaries</h1>

      {summaries.length === 0 ? (
        <div className="card-plain text-center py-12">
          <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="font-body text-slate-600">No weekly summaries available yet.</p>
          <p className="font-body text-slate-500 text-sm mt-2">
            Summaries will appear here once your child is enrolled and the teacher publishes updates.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {summaries.map(summary => (
            <div key={summary.id} className="card-plain" data-testid={`summary-${summary.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className="tag-new">Week {summary.week_number}</span>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mt-2">{summary.title}</h3>
                  <p className="font-body text-sm text-slate-500">{summary.class_name}</p>
                </div>
                {summary.published_at && (
                  <span className="font-body text-xs text-slate-500">
                    {new Date(summary.published_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              <p className="font-body text-slate-700 whitespace-pre-wrap">{summary.content}</p>
              {summary.photos && summary.photos.length > 0 && (
                <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                  {summary.photos.map((photo, idx) => (
                    <img 
                      key={idx} 
                      src={photo} 
                      alt={`Week ${summary.week_number} photo ${idx + 1}`}
                      className="w-24 h-24 object-cover rounded-lg border-2 border-slate-200"
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Waiting List Page
const WaitingListPage = () => {
  const [waitingList, setWaitingList] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchWaitingList = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/waiting-list`);
      setWaitingList(response.data);
    } catch (error) {
      console.error('Failed to fetch waiting list:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWaitingList();
  }, [fetchWaitingList]);

  const handleRemove = async (id) => {
    try {
      await axios.delete(`${API}/waiting-list/${id}`);
      toast.success('Removed from waiting list');
      fetchWaitingList();
    } catch (error) {
      toast.error('Failed to remove from waiting list');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-bold text-slate-900">Waiting List</h1>

      {waitingList.length === 0 ? (
        <div className="card-plain text-center py-12">
          <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="font-body text-slate-600">You're not on any waiting lists.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {waitingList.map(entry => (
            <div key={entry.id} className="card-plain flex items-center justify-between">
              <div>
                <h3 className="font-heading font-bold text-slate-900">{entry.child_name}</h3>
                <p className="font-body text-slate-600">{entry.class_name}</p>
                <p className="font-body text-xs text-slate-500">
                  Added: {new Date(entry.created_at).toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={() => handleRemove(entry.id)}
                className="btn-outline text-sm py-2 px-4"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Parent Info Page (within portal)
const ParentInfoPortal = () => {
  const starterPackItems = [
    { name: "A3 Polyholdall", description: "For carrying artwork" },
    { name: "A4 Sketchbook", description: "Quality sketchbook" },
    { name: "Graphite Pencil Set (6)", description: "Box of 6 pencils" },
    { name: "Oil Pastels (16)", description: "Set of 16 oil pastels" },
    { name: "Watercolour Set with Brush", description: "Complete watercolour set" },
    { name: "Coloured Pencils (12)", description: "Set of 12 coloured pencils" },
    { name: "Acrylic Paints (12)", description: "Set of 12 acrylic paints" },
    { name: "Eraser", description: "Quality eraser" },
    { name: "Sharpener", description: "Pencil sharpener" },
    { name: "Brush Set", description: "Round and flat brushes" },
    { name: "Pencil Case", description: "Durable pencil case" }
  ];

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-bold text-slate-900">Parent Information</h1>

      <div className="card-plain">
        <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">About After School Art Club</h2>
        <div className="font-body text-slate-700 space-y-3">
          <p>
            After School Art Club is designed to inspire children in <strong>Years 1–6</strong> to explore their creativity, develop artistic skills, and build confidence in a supportive environment.
          </p>
          <p>
            Each term varies in length and finishes with a <strong>children's art exhibition</strong> where parents are invited to attend and celebrate their children's achievements.
          </p>
        </div>
      </div>

      <div className="card-plain">
        <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Fees & Pricing</h2>
        <div className="grid md:grid-cols-2 gap-4 font-body">
          <div className="p-4 bg-slate-50 rounded-xl">
            <p className="font-heading font-bold text-slate-900">£8 per session</p>
            <p className="text-slate-600 text-sm">Standard rate per child</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-xl">
            <p className="font-heading font-bold text-slate-900">£7 sibling discount</p>
            <p className="text-slate-600 text-sm">Per session for 2nd+ child in same class</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-xl">
            <p className="font-heading font-bold text-slate-900">£62 returning student</p>
            <p className="text-slate-600 text-sm">Special rate per term</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-xl">
            <p className="font-heading font-bold text-slate-900">Starter Pack Included</p>
            <p className="text-slate-600 text-sm">Quality art materials provided</p>
          </div>
        </div>
      </div>

      <div className="card-plain">
        <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Starter Art Pack Contents</h2>
        <p className="font-body text-slate-600 mb-4">Every child receives these quality materials included in their term fee:</p>
        <div className="grid md:grid-cols-2 gap-2">
          {starterPackItems.map((item, index) => (
            <div key={index} className="flex items-center gap-2 p-2 font-body text-slate-700">
              <Check className="w-4 h-4 text-teal-600 flex-shrink-0" />
              <span>{item.name}</span>
            </div>
          ))}
        </div>
        <p className="font-body text-sm text-slate-500 mt-4">
          Additional materials (heavy cartridge paper, watercolour paper, pastel paper) provided during term.
        </p>
      </div>

      <div className="card-plain">
        <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">What to Bring</h2>
        <ul className="space-y-2 font-body text-slate-700">
          <li className="flex items-center gap-2">
            <Check className="w-4 h-4 text-teal-600" />
            Wear clothes that can get messy
          </li>
          <li className="flex items-center gap-2">
            <Check className="w-4 h-4 text-teal-600" />
            A healthy snack and water bottle
          </li>
          <li className="flex items-center gap-2">
            <Check className="w-4 h-4 text-teal-600" />
            Their art pack (provided at start of term)
          </li>
          <li className="flex items-center gap-2">
            <Check className="w-4 h-4 text-teal-600" />
            Enthusiasm and creativity!
          </li>
        </ul>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-plain border-l-4 border-l-pink-500">
          <h3 className="font-heading text-lg font-bold text-pink-500 mb-2">End of Term Exhibition</h3>
          <p className="font-body text-slate-700 text-sm">
            Each term concludes with a special art exhibition where parents are invited to celebrate their child's achievements.
          </p>
        </div>
        <div className="card-plain border-l-4 border-l-teal-600">
          <h3 className="font-heading text-lg font-bold text-teal-600 mb-2">Weekly Updates</h3>
          <p className="font-body text-slate-700 text-sm">
            Receive weekly summaries about what your child learned. Check the Summaries section in your portal!
          </p>
        </div>
      </div>
    </div>
  );
};

// Main Portal Component
export default function ParentPortal() {
  const location = useLocation();

  const navItems = [
    { path: '/portal', icon: <Home className="w-5 h-5" />, label: 'Dashboard', exact: true },
    { path: '/portal/children', icon: <Users className="w-5 h-5" />, label: 'Children' },
    { path: '/portal/enroll', icon: <Calendar className="w-5 h-5" />, label: 'Enroll' },
    { path: '/portal/summaries', icon: <BookOpen className="w-5 h-5" />, label: 'Summaries' },
    { path: '/portal/waiting-list', icon: <Clock className="w-5 h-5" />, label: 'Waiting List' },
    { path: '/portal/parent-info', icon: <FileText className="w-5 h-5" />, label: 'Parent Info' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-artsy-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="card-plain sticky top-24">
              <h2 className="font-heading text-lg font-bold text-slate-900 mb-4">Parent Portal</h2>
              <nav className="space-y-1">
                {navItems.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl font-body font-semibold transition-colors ${
                      isActive(item.path, item.exact)
                        ? 'bg-teal-600 text-white'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <Routes>
              <Route index element={<Dashboard />} />
              <Route path="children" element={<ChildrenPage />} />
              <Route path="enroll" element={<EnrollPage />} />
              <Route path="summaries" element={<SummariesPage />} />
              <Route path="waiting-list" element={<WaitingListPage />} />
              <Route path="parent-info" element={<ParentInfoPortal />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
}
